import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { MessageSquare, Send, ArrowLeft, User, Loader2, Package } from 'lucide-react';

export default function MessagesPage() {
  const { convId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activeConv, setActiveConv] = useState(convId || null);
  const [newMsg, setNewMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    api.get('/conversations').then(r => {
      setConversations(r.data);
      if (convId) setActiveConv(convId);
      else if (r.data.length > 0 && !activeConv) setActiveConv(r.data[0].conversation_id);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!activeConv) return;
    api.get(`/conversations/${activeConv}/messages`).then(r => setMessages(r.data)).catch(() => {});
    const interval = setInterval(() => {
      api.get(`/conversations/${activeConv}/messages`).then(r => setMessages(r.data)).catch(() => {});
    }, 5000);
    return () => clearInterval(interval);
  }, [activeConv]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMsg.trim() || !activeConv) return;
    setSending(true);
    try {
      await api.post(`/conversations/${activeConv}/messages`, { text: newMsg });
      setNewMsg('');
      const { data } = await api.get(`/conversations/${activeConv}/messages`);
      setMessages(data);
      const { data: convs } = await api.get('/conversations');
      setConversations(convs);
    } catch {} finally {
      setSending(false);
    }
  };

  const activeConvData = conversations.find(c => c.conversation_id === activeConv);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="min-h-[calc(100vh-4rem)]" data-testid="messages-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-6">
        <h1 className="text-2xl font-bold text-[#2C362F] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Messages</h1>

        {conversations.length === 0 ? (
          <div className="text-center py-20 bg-white border border-[#E8E6DF] rounded-2xl">
            <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-lg font-medium text-[#2C362F] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>No messages yet</p>
            <p className="text-sm text-muted-foreground mb-4">Start a conversation by contacting a seller</p>
            <Link to="/browse"><Button className="rounded-full bg-primary text-white">Browse Items</Button></Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-12rem)]">
            {/* Conversations List */}
            <div className="bg-white border border-[#E8E6DF] rounded-2xl overflow-hidden" data-testid="conversations-list">
              <div className="overflow-y-auto h-full">
                {conversations.map(conv => (
                  <button
                    key={conv.conversation_id}
                    onClick={() => setActiveConv(conv.conversation_id)}
                    className={`w-full text-left p-4 border-b border-[#E8E6DF] last:border-0 transition-colors hover:bg-[#F4F1EA] ${activeConv === conv.conversation_id ? 'bg-[#F4F1EA]' : ''}`}
                    data-testid={`conv-item-${conv.conversation_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
                        {conv.other_user?.picture ? (
                          <img src={conv.other_user.picture} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <User className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm text-[#2C362F] truncate">{conv.other_user?.name || 'User'}</span>
                          {conv.unread_count > 0 && (
                            <span className="w-5 h-5 bg-primary rounded-full text-white text-[10px] flex items-center justify-center flex-shrink-0">{conv.unread_count}</span>
                          )}
                        </div>
                        {conv.listing && (
                          <p className="text-xs text-primary truncate flex items-center gap-1">
                            <Package className="w-3 h-3" strokeWidth={1.5} />{conv.listing.title}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground truncate">{conv.last_message}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Messages */}
            <div className="lg:col-span-2 bg-white border border-[#E8E6DF] rounded-2xl flex flex-col overflow-hidden" data-testid="message-thread">
              {activeConvData ? (
                <>
                  <div className="p-4 border-b border-[#E8E6DF] flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center overflow-hidden">
                      {activeConvData.other_user?.picture ? (
                        <img src={activeConvData.other_user.picture} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-sm">{activeConvData.other_user?.name || 'User'}</p>
                      {activeConvData.listing && (
                        <Link to={`/listings/${activeConvData.listing_id}`} className="text-xs text-primary hover:underline">
                          Re: {activeConvData.listing.title}
                        </Link>
                      )}
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {messages.map(msg => (
                      <div key={msg.message_id} className={`flex ${msg.sender_id === user.user_id ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${
                          msg.sender_id === user.user_id
                            ? 'bg-primary text-white rounded-br-md'
                            : 'bg-[#F4F1EA] text-[#2C362F] rounded-bl-md'
                        }`} data-testid={`message-${msg.message_id}`}>
                          <p>{msg.text}</p>
                          <p className={`text-[10px] mt-1 ${msg.sender_id === user.user_id ? 'text-white/60' : 'text-muted-foreground'}`}>
                            {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>

                  <form onSubmit={handleSend} className="p-4 border-t border-[#E8E6DF] flex gap-2">
                    <Input
                      value={newMsg}
                      onChange={e => setNewMsg(e.target.value)}
                      placeholder="Type a message..."
                      className="rounded-xl bg-[#FDFBF7] border-[#E8E6DF] h-10 flex-1"
                      data-testid="message-input"
                    />
                    <Button type="submit" disabled={sending || !newMsg.trim()} size="icon" className="rounded-xl bg-primary text-white h-10 w-10" data-testid="send-message-btn">
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={1.5} />}
                    </Button>
                  </form>
                </>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  Select a conversation
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
